import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Signal, ViewChild, ViewEncapsulation, WritableSignal, effect, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule, MatSliderThumb } from '@angular/material/slider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-confirm-map',
  templateUrl: './confirm-map.component.html',
  styleUrls: ['./confirm-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    TranslateModule,
    ButtonComponent,
    MatSliderModule,
    MatSliderThumb
  ]
})

export class ConfirmMapComponent implements OnDestroy, AfterViewInit {
  // Components API
  @ViewChild("input") input!: ElementRef;
  @Input() data: Signal<any | null> = signal<any | null>(null);
  @Input() editedData: Signal<any | null> = signal<any | null>(null);
  @Input() showFloatingWindow: boolean = false;
  @Input() title: string = "viewer.zone.editing";
  @Input() icon: string = "";
  @Input() revertKeys: string[] = [];
  @Input() angle: number = 0;
  @Output() cancel: EventEmitter<any> = new EventEmitter<any>();
  @Output() saveData: EventEmitter<any> = new EventEmitter<any>();
  @Output() rotation: EventEmitter<any> = new EventEmitter<any>();
  @Output() opacity: EventEmitter<number> = new EventEmitter<number>();
  @Output() opacityCommit = new EventEmitter<number>();

  // Signals
  incrementSignal: WritableSignal<boolean> = signal<boolean>(false);
  decrementSignal: WritableSignal<boolean> = signal<boolean>(false);
  opacitySignal: WritableSignal<number> = signal<number>(100);

  // Variables
  dataSub?: Subscription;
  editedDataSub?: Subscription;
  incrementInterval: any;
  decrementInterval: any;
  incrementTimeout: any;
  decrementTimeout: any;
  destroy$ = new Subject<void>();
  private opacityThrottleTimeout: any = null;

  constructor() {
    // Effect : Sync opacity from editedData
    effect(() => {
      const edited = this.editedData();
      const opacityValue = edited?.imageTransform?.opacity ?? edited?.opacity;

      if (opacityValue !== undefined && opacityValue !== null) {
        this.opacitySignal.set(this.toOpacityPercent(opacityValue));
      }
    });

    // Effect : Manage increments and decrements
    effect(() => {
      const increment = this.incrementSignal();

      if (increment) {
        this.incrementInterval = setInterval(() => {
          this.increment();
        }, 200);
      } else if (this.incrementInterval) {
        clearInterval(this.incrementInterval);
        this.incrementInterval = null;
      }

      const decrement = this.decrementSignal();

      if (decrement) {
        this.decrementInterval = setInterval(() => {
          this.decrement();
        }, 200);
      } else if (this.decrementInterval) {
        clearInterval(this.decrementInterval);
        this.decrementInterval = null;
      }
    });
  }

  ngAfterViewInit() {
    if (this.input) {
      this.angle = this.normalizeAngle(this.angle);
      this.input.nativeElement.value = `${this.angle}°`;
    }
  }

  save() {
    this.saveData.emit(this.editedData());
  }

  handleCancel() {
    this.revertKeys.forEach(key => {
      this.editedData()[key].setMap(null);
    })

    this.cancel.emit(this.data());
  }

  ngOnDestroy(): void {
    if (this.dataSub) {
      this.dataSub.unsubscribe();
      this.dataSub = undefined;
    }

    if (this.editedDataSub) {
      this.editedDataSub.unsubscribe();
      this.editedDataSub = undefined;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  onInputChange(event: any) {
    this.angle = this.normalizeAngle(event.target.value);
    event.target.value = this.angle;
    this.rotation.emit(this.angle);
  }

  onInputFocus(event: any) {
    event.target.value = this.normalizeAngle(event.target.value).toString();
  }

  onInputBlur(event: any) {
    this.angle = this.normalizeAngle(event.target.value);
    event.target.value = `${this.angle}°`;
    this.rotation.emit(this.angle);
  }

  // ============== Angle Methods ============== //
  decrement() {
    this.angle = (this.angle + 359) % 360;
    this.emitAngle();
  }

  increment() {
    this.angle = (this.angle + 1) % 360;
    this.emitAngle();
  }

  doIncrements() {
    this.increment();

    this.incrementTimeout = setTimeout(() => {
      this.incrementSignal.set(true);
    }, 1000);

  }

  stopIncrements() {
    clearTimeout(this.incrementTimeout);
    if (this.incrementSignal())
      this.incrementSignal.set(false);
  }

  doDecrements() {
    this.decrement();

    this.decrementTimeout = setTimeout(() => {
      this.decrementSignal.set(true);
    }, 1000);

  }

  stopDecrements() {
    clearTimeout(this.decrementTimeout);
    if (this.decrementSignal())
      this.decrementSignal.set(false);
  }

  private emitAngle(): void {
    if (this.input) {
      this.input.nativeElement.value = `${this.angle}°`;
    }

    this.rotation.emit(this.angle);
  }

  private normalizeAngle(value: unknown): number {
    const normalizedValue = String(value ?? '').replace(',', '.').replace(/[^\d.-]/g, '');
    const parsed = Math.trunc(Number.parseFloat(normalizedValue));

    if (Number.isNaN(parsed))
      return 0;

    // Normalize to [0..359]
    return ((parsed % 360) + 360) % 360;
  }

  // ============== Opacity management ============== //
  onOpacityInput(event: Event): void {
    const value = Math.round(Number((event.target as HTMLInputElement).value));
    this.opacitySignal.set(value);
    this.opacity.emit(value);
  }

  onOpacityCommit(event: Event): void {
    const value = Math.round(Number((event.target as HTMLInputElement).value));
    this.opacityCommit.emit(value);
  }

  private toOpacityPercent(value: unknown, fromRatio = true): number {
    const parsed = Number.parseFloat(String(value ?? ''));
    if (!Number.isFinite(parsed)) return 100;

    const percent = fromRatio ? parsed * 100 : parsed;
    return Math.round(percent);
  }
}
